import { observeElementRect, useVirtualizer } from "@tanstack/react-virtual"
import { useTranslation } from "react-i18next"
import { atom, useAtomValue } from "jotai"
import { memo, useEffect, useLayoutEffect, useRef } from "react"
import { MemoizedThumbnail } from "@/components/ui/Thumbnail"
import { useVisibleThumbnails } from "@/utils/useVisibleThumbnails"
import clsx from "clsx"
import {
	currentPhotoAtom,
	displayedPhotoIdsAtom,
	displayedPhotosAtom,
	selectedPhotosAtom,
} from "@/store/photo/photos-state"
import {
	activeFiltersKeyAtom,
	resetAllFilters,
	gridColAtom,
	gridSizeAtom,
	sortOptionAtom,
} from "@/store/photo/photos-view-state"
import { batchLock, batchMarkReference } from "@/store/style/reference"
import { ScrollBar } from "@/components/ui/ScrollBar"
import { Button } from "./ui/Button"
import { isCullingModeAtom, isManualModeAtom, projectAtom } from "@/store/project/projects-state"
import { useViewTransition, commitTransition } from "@/utils/useViewTransition"
import { isPhotoFullscreenAtom } from "@/store/ui-views-state"

const isBatchReferenceAvailableAtom = atom((get) => {
	const selectedPhotos = get(selectedPhotosAtom)

	if (selectedPhotos.length == 0) {
		return false
	} else {
		for (const photo of selectedPhotos) {
			if (photo.kind != "reference") {
				return true
			}
		}

		return false
	}
})

const isBatchLockAvailableAtom = atom((get) => {
	const selectedPhotos = get(selectedPhotosAtom)

	if (selectedPhotos.length == 0) {
		return false
	} else {
		for (const photo of selectedPhotos) {
			if (photo.kind != "reference" && photo.kind != "locked") {
				return true
			}
		}
		return false
	}
})

export const Gallery = memo(() => {
	const { t } = useTranslation("components")
	const { currentView, isTransitioningTo } = useViewTransition()
	const displayedPhotos = useAtomValue(displayedPhotosAtom)
	const displayedPhotoIds = useAtomValue(displayedPhotoIdsAtom)
	const selectedPhotos = useAtomValue(selectedPhotosAtom)
	const activeFiltersKey = useAtomValue(activeFiltersKeyAtom)
	const isPhotoFullscreen = useAtomValue(isPhotoFullscreenAtom)
	const isShown = currentView === "grid"
	const isVisible = isShown && !isPhotoFullscreen
	const isTransitioning = isTransitioningTo("grid") && !isPhotoFullscreen

	const parentRef = useRef<HTMLDivElement>(null)
	const parentSize = useRef(0)
	const isPhotoFullscreenRef = useRef(isPhotoFullscreen)
	isPhotoFullscreenRef.current = isPhotoFullscreen

	const isBatchReferenceAvailable = useAtomValue(isBatchReferenceAvailableAtom)
	const isBatchLockAvailable = useAtomValue(isBatchLockAvailableAtom)
	const isManualMode = useAtomValue(isManualModeAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)

	const gridSize = useAtomValue(gridSizeAtom)
	const gridCol = useAtomValue(gridColAtom)
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const project = useAtomValue(projectAtom)
	const sortOption = useAtomValue(sortOptionAtom)

	const getSize = () => {
		return (parentSize.current - 4) / gridCol
	}

	const measureVirtualizers = () => {
		if (isPhotoFullscreenRef.current) {
			return
		}

		parentSize.current = rowVirtualizer.scrollElement?.clientWidth ?? 0

		rowVirtualizer.measure()
		columnVirtualizer.measure()
	}

	const updateSize = () => {
		measureVirtualizers()

		if (isVisible) {
			scrollToCurrentFile(false)
		}
	}

	const renderCell = (index: number) => {
		const photo = displayedPhotos[index]
		if (photo && index < displayedPhotos.length && project) {
			return (
				<MemoizedThumbnail
					size={gridSize > 5 ? "medium" : "small"}
					photo={photo}
					project={project}
					isStackedPhoto={false}
				/>
			)
		}
	}

	const rowVirtualizer = useVirtualizer({
		count: Math.max(Math.ceil(displayedPhotos.length / gridCol), 1),
		getScrollElement: () => parentRef.current,
		estimateSize: () => getSize(),
		observeElementRect: (instance, cb) => {
			observeElementRect(instance, (rect) => {
				if (isPhotoFullscreenRef.current) {
					return
				}

				updateSize()
				cb(rect)
			})
		},
		overscan: 0,
	})

	const columnVirtualizer = useVirtualizer({
		horizontal: true,
		count: gridCol,
		getScrollElement: () => parentRef.current,
		estimateSize: () => getSize(),
		overscan: 0,
	})

	useLayoutEffect(() => {
		if (isVisible) {
			scrollToCurrentFileAfterVisibilityRestore(false, true)
		}
	}, [isVisible])

	useEffect(() => {
		rowVirtualizer.measure()
		columnVirtualizer.measure()
	}, [rowVirtualizer, columnVirtualizer, gridCol])

	// When transitioning to grid, scroll to current file synchronously, then commit in next frame
	// This ensures the scroll position is set and painted (invisible) before we reveal it
	useLayoutEffect(() => {
		if (isTransitioning) {
			scrollToCurrentFileAfterLayout(true)
			requestAnimationFrame(() => {
				commitTransition()
			})
		}
	}, [isTransitioning])

	// Scroll to current file when sort option changes
	useLayoutEffect(() => {
		if (isVisible) {
			scrollToCurrentFileAfterLayout(true)
		}
	}, [sortOption])

	// Handle gridCol and filter changes when already visible
	useEffect(() => {
		if (isVisible) {
			scrollToCurrentFileAfterLayout(true)
		}
	}, [gridCol, activeFiltersKey])

	// Scroll to current photo when selection changes
	useLayoutEffect(() => {
		if (isVisible) {
			scrollToCurrentFileAfterLayout(false)
		}
	}, [currentPhoto?.id, isVisible])

	useEffect(() => {
		if (isVisible) {
			scrollToCurrentFileAfterLayout(false)
		}
	}, [displayedPhotoIds, isVisible])

	const scrollToCurrentFileAfterLayout = (shouldCenter: boolean, force = false) => {
		requestAnimationFrame(() => {
			measureVirtualizers()
			scrollToCurrentFile(shouldCenter, force)
		})
	}

	const scrollToCurrentFileAfterVisibilityRestore = (shouldCenter: boolean, force = false) => {
		scrollToCurrentFileAfterLayout(shouldCenter, force)

		// After preview fullscreen exits, the grid can be visible before virtual
		// rows reflect the restored viewport and culling-updated list.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				measureVirtualizers()
				scrollToCurrentFile(shouldCenter, force)
			})
		})
	}

	const scrollToCurrentFile = (shouldCenter: boolean, force = false) => {
		const currentIndex = displayedPhotos.findIndex((photo) => photo.id === currentPhoto?.id)
		if (currentIndex < 0) {
			return
		}

		const currentRow = Math.floor(currentIndex / gridCol)
		const scrollElement = rowVirtualizer.scrollElement
		const viewportHeight = scrollElement?.clientHeight ?? 0
		const scrollOffset = rowVirtualizer.scrollOffset ?? 0
		const BOTTOM_GUTTER = 2
		const viewportEnd = Math.max(scrollOffset + viewportHeight - BOTTOM_GUTTER, scrollOffset)
		const currentVirtualRow = rowVirtualizer
			.getVirtualItems()
			.find((item) => item.index === currentRow)

		// Scroll whenever any portion of the selected row is clipped from view.
		if (currentVirtualRow && viewportHeight > 0) {
			const rowStart = currentVirtualRow.start
			const rowEnd = currentVirtualRow.end
			const totalSize = rowVirtualizer.getTotalSize()
			const maxScrollOffset = Math.max(totalSize - viewportHeight + BOTTOM_GUTTER, 0)

			if (rowStart < scrollOffset) {
				if (shouldCenter) {
					rowVirtualizer.scrollToIndex(currentRow, { align: "center" })
				} else {
					const targetOffset = Math.max(rowStart, 0)
					rowVirtualizer.scrollToOffset(targetOffset, { align: "start" })
				}
				return
			}

			if (rowEnd > viewportEnd) {
				if (shouldCenter) {
					rowVirtualizer.scrollToIndex(currentRow, { align: "center" })
				} else {
					const targetOffset = Math.min(
						rowEnd - viewportHeight + BOTTOM_GUTTER,
						maxScrollOffset,
					)
					rowVirtualizer.scrollToOffset(Math.max(targetOffset, 0), { align: "start" })
				}
				return
			}

			return
		}

		if (force) {
			rowVirtualizer.scrollToIndex(currentRow, { align: shouldCenter ? "center" : "end" })
			return
		}

		if (currentRow < (rowVirtualizer.range?.startIndex ?? 0)) {
			rowVirtualizer.scrollToIndex(currentRow, { align: shouldCenter ? "center" : "start" })
		} else if (currentRow > (rowVirtualizer.range?.endIndex ?? 0)) {
			rowVirtualizer.scrollToIndex(currentRow, { align: shouldCenter ? "center" : "end" })
		}
	}

	const rows = rowVirtualizer.getVirtualItems()
	const cols = columnVirtualizer.getVirtualItems()

	const visibleRows = rows.filter(
		(row) =>
			row.index >= (rowVirtualizer.range?.startIndex ?? 0) &&
			row.index <= (rowVirtualizer.range?.endIndex ?? 0),
	)

	useVisibleThumbnails({
		rows: visibleRows,
		cols,
		photoIds: displayedPhotoIds,
		isShown: isVisible,
	})

	return (
		<div aria-label={t("gallery.ariaGallery")} className="flex size-full flex-col bg-base-800">
			{!isManualMode && !isCullingMode && (
				<div className="flex w-full flex-row items-center justify-start space-x-3 bg-base-700 px-3 py-2 text-xs">
					<div className="text-sm">
						{t("gallery.selectedCount", { count: selectedPhotos.length })}
					</div>
					<div className="h-full w-px bg-base-500" />

					<div className="flex flex-row space-x-2">
						<Button
							variant="ghost"
							size="custom"
							trackingLabel="batch_mark_reference"
							aria-label={t("gallery.ariaMarkPhotoAsReference")}
							trackingValues={{
								projectId: project?.id,
								photoCount: selectedPhotos.filter((p) => p.kind != "reference")
									.length,
							}}
							className={clsx(
								isBatchReferenceAvailable ? "hover:bg-base-600" : "opacity-25",
								"flex flex-row space-x-1 rounded-md bg-base-700 px-3 py-1.5 text-xs",
							)}
							aria-disabled={!isBatchReferenceAvailable}
							onClick={batchMarkReference}
						>
							<div className="rounded-sm bg-base-400 px-2 text-base-800">M</div>
							<div>{t("gallery.markReference")}</div>
						</Button>

						<Button
							variant="ghost"
							size="custom"
							trackingLabel="batch_lock_editing"
							aria-label={t("gallery.ariaToggleBatchPhotoEditLock")}
							className={clsx(
								isBatchLockAvailable ? "hover:bg-base-600" : "opacity-25",
								"flex flex-row space-x-1 rounded-md bg-base-700 px-3 py-1.5 text-xs",
							)}
							aria-disabled={!isBatchLockAvailable}
							onClick={batchLock}
						>
							<div className="rounded-sm bg-base-400 px-2 text-base-800">L</div>
							<div>{t("gallery.lockEditing")}</div>
						</Button>
					</div>
				</div>
			)}
			<div
				ref={parentRef}
				role="grid"
				aria-label={t("gallery.ariaPhotoGallery", { count: displayedPhotos.length })}
				aria-rowcount={Math.ceil(displayedPhotos.length / gridCol)}
				aria-colcount={gridCol}
				className="no-scrollbar relative flex size-full justify-end overflow-y-auto bg-base-800 p-0.5"
			>
				{displayedPhotos.length === 0 && (
					<div className="absolute z-20 flex size-full flex-col items-center justify-center">
						<div className="m-1 text-sm text-base-400">
							{t("gallery.noPhotosMatchingFilters")}
						</div>
						<Button
							trackingLabel="reset_filters"
							aria-label={t("gallery.ariaResetFilters")}
							size="sm"
							className="mt-2"
							onClick={() => {
								resetAllFilters()
							}}
						>
							{t("gallery.resetFilters")}
						</Button>
					</div>
				)}
				<div
					style={{
						height: `${rowVirtualizer.getTotalSize()}px`,
						width: `${columnVirtualizer.getTotalSize()}px`,
						position: "relative",
					}}
				>
					{rows.map((virtualRow) => (
						<div key={virtualRow.index}>
							{cols.map((virtualColumn) => {
								const photoIndex = virtualRow.index * gridCol + virtualColumn.index
								const photo = displayedPhotos[photoIndex]
								return (
									<div
										key={
											photo?.id ??
											`${virtualRow.index}-${virtualColumn.index}`
										}
										className="absolute left-0 top-0 flex p-0.5"
										style={{
											width: `${virtualColumn.size}px`,
											height: `${virtualRow.size}px`,
											transform: `translateX(${virtualColumn.start}px) translateY(${virtualRow.start}px)`,
										}}
									>
										{renderCell(photoIndex)}
									</div>
								)
							})}
						</div>
					))}
				</div>
				<ScrollBar />
			</div>
		</div>
	)
})
